<template>
  <!-- Origem Flow: como nas demais views, o seletor do campo de data vira
       pill teal com o NOME do campo ("Início") em primeiro na toolbar. -->
  <ul
    v-if="!tableLoading"
    class="header__filter header__filter--full-width header__filter--view-first"
  >
    <li class="header__filter-item">
      <a
        class="header__filter-link"
        :class="{
          'header__filter-link--disabled': !canChooseDateField,
          'active active--purple': hasValidDateField,
        }"
        @click="showChooseDateFieldModal"
      >
        <i class="header__filter-icon iconoir-calendar"></i>
        <span class="header__filter-name">
          {{ dateFieldPillText }}
        </span>
      </a>
      <SelectDateFieldModal
        ref="selectDateFieldModal"
        :view="view"
        :table="table"
        :fields="fields"
        :database="database"
        :date-field-id="dateFieldId(fields)"
        @refresh="$emit('refresh', $event)"
      >
      </SelectDateFieldModal>
    </li>
    <!-- Origem Flow: sincronização bidirecional com o Google Calendar. Na
         tabela "Todas as tarefas" o chip é o de todas as tabelas (extra da
         toolbar), não o desta tabela. -->
    <li v-if="!readOnly && !table.all_tasks" class="header__filter-item">
      <a
        ref="gcalLink"
        class="header__filter-link"
        :class="{ 'active active--purple': gcalTableConnected && gcal.active }"
        @click="toggleGcalPanel()"
      >
        <i class="header__filter-icon iconoir-google"></i>
        <span class="header__filter-name">{{
          $t('calendarGoogleSync.pill')
        }}</span>
      </a>
      <Context ref="gcalContext" overflow-scroll max-height-if-outside-viewport>
        <div class="gcal-sync-panel">
          <div class="gcal-sync-panel__title">
            {{ $t('calendarGoogleSync.title') }}
          </div>
          <!-- Estado 1: sem conta Google conectada -> OAuth. -->
          <template v-if="!gcal.accountConnected">
            <p class="gcal-sync-panel__text">
              {{ $t('calendarGoogleSync.explanation') }}
            </p>
            <Button
              type="primary"
              :loading="gcal.loading"
              :disabled="dateFieldId(fields) == null"
              @click="connectGcal()"
            >
              {{ $t('calendarGoogleSync.connect') }}
            </Button>
            <p
              v-if="dateFieldId(fields) == null"
              class="gcal-sync-panel__text gcal-sync-panel__text--muted"
            >
              {{ $t('calendarGoogleSync.needsDateField') }}
            </p>
          </template>
          <!-- Conta inativa (token revogado): reconectar via OAuth. -->
          <template v-else-if="!gcal.active">
            <p class="gcal-sync-panel__text gcal-sync-panel__text--muted">
              {{ $t('calendarGoogleSync.inactive') }}
            </p>
            <Button
              type="primary"
              :loading="gcal.loading"
              :disabled="dateFieldId(fields) == null"
              @click="connectGcal()"
            >
              {{ $t('calendarGoogleSync.reconnect') }}
            </Button>
          </template>
          <!-- Estado 2: conta ativa, esta tabela ainda não sincroniza. -->
          <template v-else-if="!gcalTableConnected">
            <p class="gcal-sync-panel__text">
              {{ $t('calendarGoogleSync.accountConnectedTableNot') }}
            </p>
            <Button
              type="primary"
              :loading="gcal.loading"
              :disabled="dateFieldId(fields) == null"
              @click="connectThisTable()"
            >
              {{ $t('calendarGoogleSync.connectThisTable') }}
            </Button>
            <p
              v-if="dateFieldId(fields) == null"
              class="gcal-sync-panel__text gcal-sync-panel__text--muted"
            >
              {{ $t('calendarGoogleSync.needsDateField') }}
            </p>
          </template>
          <!-- Estado 3: tabela vinculada. -->
          <template v-else>
            <p class="gcal-sync-panel__text">
              <i class="iconoir-check-circle gcal-sync-panel__ok"></i>
              {{ $t('calendarGoogleSync.connected') }}
            </p>
            <p
              v-if="gcal.lastSyncedAt"
              class="gcal-sync-panel__text gcal-sync-panel__text--muted"
            >
              {{
                $t('calendarGoogleSync.lastSync', {
                  time: new Date(gcal.lastSyncedAt).toLocaleString(),
                })
              }}
            </p>
            <template v-if="gcal.connections.length > 1">
              <p class="gcal-sync-panel__text gcal-sync-panel__text--muted">
                {{ $t('calendarGoogleSync.defaultTableLabel') }}
              </p>
              <Dropdown
                :model-value="gcal.defaultTableId"
                :show-search="false"
                :disabled="gcal.loading"
                class="gcal-sync-panel__default-table margin-bottom-1"
                @update:model-value="setDefaultTable($event)"
              >
                <DropdownItem
                  v-for="conn in gcal.connections"
                  :key="conn.table_id"
                  :name="conn.table_name"
                  :value="conn.table_id"
                ></DropdownItem>
              </Dropdown>
            </template>
            <Button
              type="secondary"
              :loading="gcal.loading"
              @click="disconnectThisTable()"
            >
              {{ $t('calendarGoogleSync.disconnectTable') }}
            </Button>
            <ButtonText
              class="gcal-sync-panel__disconnect-account"
              type="secondary"
              @click="disconnectAccount()"
            >
              {{ $t('calendarGoogleSync.disconnectAccount') }}
            </ButtonText>
          </template>
        </div>
      </Context>
    </li>
    <li v-if="dateFieldId(fields) != null" class="header__filter-item">
      <a
        ref="customizeContextLink"
        class="header__filter-link"
        @click="
          $refs.customizeContext.toggle(
            $refs.customizeContextLink,
            'bottom',
            'left',
            4
          )
        "
      >
        <i class="header__filter-icon iconoir-list"></i>
        <span class="header__filter-name">{{
          $t('calendarViewHeader.labels')
        }}</span>
      </a>
      <ViewFieldsContext
        ref="customizeContext"
        :database="database"
        :view="view"
        :fields="fields"
        :field-options="fieldOptions"
        :allow-cover-image-field="false"
        @update-all-field-options="updateAllFieldOptions"
        @update-field-options-of-field="updateFieldOptionsOfField"
        @update-order="orderFieldOptions"
      ></ViewFieldsContext>
    </li>
    <li class="header__filter-item header__filter-item--full-width">
      <ViewSearch
        :view="view"
        :fields="fields"
        :store-prefix="storePrefix"
        :always-hide-rows-not-matching-search="true"
        @refresh="$emit('refresh', $event)"
      ></ViewSearch>
    </li>
  </ul>
</template>

<script>
import { mapState } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import SelectDateFieldModal from '@baserow_premium/components/views/calendar/SelectDateFieldModal'
import ViewFieldsContext from '@baserow/modules/database/components/view/ViewFieldsContext'
import ViewSearch from '@baserow/modules/database/components/view/ViewSearch'
import GoogleCalendarService from '@baserow/modules/core/services/googleCalendar'

export default {
  name: 'CalendarViewHeader',
  emits: ['refresh'],
  components: {
    ViewFieldsContext,
    SelectDateFieldModal,
    ViewSearch,
  },
  props: {
    storePrefix: {
      type: String,
      default: '',
    },
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      gcal: {
        accountConnected: false,
        active: false,
        lastSyncedAt: null,
        defaultTableId: null,
        connections: [],
        thisTable: null,
        loading: false,
      },
    }
  },
  async mounted() {
    if (this.dateFieldId(this.fields) == null) {
      this.showChooseDateFieldModal()
    }
    await this.refreshGcalStatus()
    if (this.$route.query.gcal === 'connected') {
      this.$store.dispatch('toast/success', {
        title: this.$t('calendarGoogleSync.connectedToastTitle'),
        message: this.$t('calendarGoogleSync.connectedToast'),
      })
    }
  },
  computed: {
    gcalTableConnected() {
      return this.gcal.thisTable != null && this.gcal.thisTable.connected
    },
    timezone() {
      return this.$store.getters[`${this.storePrefix}view/calendar/getTimeZone`]
    },
    fieldOptions() {
      return this.$store.getters[
        `${this.storePrefix}view/calendar/getAllFieldOptions`
      ]
    },
    dateFieldId() {
      return this.$store.getters[
        `${this.storePrefix}view/calendar/getDateFieldIdIfNotTrashed`
      ]
    },
    getDateField() {
      return this.$store.getters[
        `${this.storePrefix}view/calendar/getDateField`
      ]
    },
    selectDateFieldLinkText() {
      const df = this.getDateField(this.fields)
      if (
        !df ||
        this.$registry.get('field', df.type).canRepresentDate(df) === false
      ) {
        return this.$t('calendarViewHeader.displayBy')
      } else {
        return this.$t('calendarViewHeader.displayedBy', {
          fieldName: this.displayedByFieldName,
        })
      }
    },
    hasValidDateField() {
      const df = this.getDateField(this.fields)
      return !!df && this.$registry.get('field', df.type).canRepresentDate(df)
    },
    /**
     * Origem Flow: com campo válido o pill mostra só o nome do campo,
     * como os toggles de agrupamento das outras views.
     */
    dateFieldPillText() {
      return this.hasValidDateField
        ? this.displayedByFieldName
        : this.$t('calendarViewHeader.displayBy')
    },
    displayedByFieldName() {
      for (let i = 0; i < this.fields.length; i++) {
        if (this.fields[i].id === this.view.date_field) {
          return this.fields[i].name
        }
      }
      return ''
    },
    isDev() {
      return import.meta.env.MODE === 'development'
    },
    ...mapState({
      tableLoading: (state) => state.table.loading,
    }),
    canChooseDateField() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.view.update',
          this.view,
          this.database.workspace.id
        )
      )
    },
  },
  watch: {
    fields() {
      const df = this.getDateField(this.fields)
      if (
        !df ||
        this.$registry.get('field', df.type).canRepresentDate(df) === false
      ) {
        this.showChooseDateFieldModal()
      }
    },
  },
  methods: {
    toggleGcalPanel() {
      this.$refs.gcalContext.toggle(this.$refs.gcalLink, 'bottom', 'left', 4)
    },
    async refreshGcalStatus() {
      // Estado da conta E do vínculo DESTA tabela (modelo conta/vínculo).
      try {
        const { data } = await GoogleCalendarService(this.$client).status(
          this.table.id
        )
        this.gcal.accountConnected = data.account_connected
        this.gcal.active = data.active || false
        this.gcal.lastSyncedAt = data.last_synced_at || null
        this.gcal.defaultTableId = data.default_table_id || null
        this.gcal.connections = data.connections || []
        this.gcal.thisTable = data.this_table || null
      } catch (error) {
        // Painel segue como "não conectado".
      }
    },
    async connectGcal() {
      this.gcal.loading = true
      try {
        const { data } = await GoogleCalendarService(this.$client).authUrl(
          this.table.id,
          this.dateFieldId(this.fields),
          this.$route.path
        )
        window.location.href = data.auth_url
      } catch (error) {
        this.gcal.loading = false
        notifyIf(error, 'view')
      }
    },
    async connectThisTable() {
      this.gcal.loading = true
      try {
        await GoogleCalendarService(this.$client).connectTable(
          this.table.id,
          this.dateFieldId(this.fields)
        )
        await this.refreshGcalStatus()
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.gcal.loading = false
      }
    },
    async disconnectThisTable() {
      this.gcal.loading = true
      try {
        await GoogleCalendarService(this.$client).disconnectTable(this.table.id)
        await this.refreshGcalStatus()
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.gcal.loading = false
      }
    },
    async setDefaultTable(tableId) {
      if (tableId === this.gcal.defaultTableId) {
        return
      }
      this.gcal.loading = true
      try {
        await GoogleCalendarService(this.$client).setDefaultTable(tableId)
        this.gcal.defaultTableId = tableId
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.gcal.loading = false
      }
    },
    async disconnectAccount() {
      this.gcal.loading = true
      try {
        await GoogleCalendarService(this.$client).disconnectAccount()
        await this.refreshGcalStatus()
      } catch (error) {
        notifyIf(error, 'view')
      } finally {
        this.gcal.loading = false
      }
    },
    showChooseDateFieldModal() {
      if (this.canChooseDateField && this.$refs.selectDateFieldModal) {
        this.$refs.selectDateFieldModal.show()
      }
    },
    async updateAllFieldOptions({ newFieldOptions, oldFieldOptions }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/calendar/updateAllFieldOptions',
          {
            newFieldOptions,
            oldFieldOptions,
            readOnly:
              this.readOnly ||
              !this.$hasPermission(
                'database.table.view.update_field_options',
                this.view,
                this.database.workspace.id
              ),
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateFieldOptionsOfField({ field, values, oldValues }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/calendar/updateFieldOptionsOfField',
          {
            field,
            values,
            oldValues,
            readOnly:
              this.readOnly ||
              !this.$hasPermission(
                'database.table.view.update_field_options',
                this.view,
                this.database.workspace.id
              ),
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async orderFieldOptions({ order }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/calendar/updateFieldOptionsOrder',
          {
            order,
            readOnly: this.readOnly,
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
  },
}
</script>
